import { useContext, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'next-i18next';
import { Breadcrumbs, Button, Grid, GridItem, ThemeContext, useHtmlTag } from '@forma/forma-ui-kit';
import Link from '@/components/Link';
import MetaTags from '@/components/MetaTags';
import TextBlockForSeo from '@/components/SeoBlocks/TextBlockForSeo';
import Element from '@/components/SeoBlocks/parts/Element';
import SearchBlock from '@/components/SeoBlocks/SearchBlock';
import SocialMediaButtons from '@/components/SeoBlocks/parts/SocialMediaButtons/SocialMediaButtons';
import useMetaTags from '@/hooks/useMetaTags';

import styles from './index.module.css';

const FolderPage = ({ templates, seoMain, folder, breadCrumbsLinks }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { viewport, lang } = useContext(ThemeContext);
  const { name, seo, seoName } = folder;

  const isPhone = viewport === 'phone';

  useHtmlTag('script', 'https://usocial.pro/usocial/usocial.js?uid=cd1204b657f67871&v=6.1.5');
  useHtmlTag('script', 'https://yastatic.net/share2/share.js');

  const thisPath = router.query.folders.join('/');

  const breadcrumbItems = [
    { name: t('homepage'), href: '/templates', as: Link },
  ];

  for (let i = 0; i < breadCrumbsLinks.length; i++) {
    breadcrumbItems.push({
      name: breadCrumbsLinks[i].name,
      href: `/templates/${breadCrumbsLinks[i].path}`,
      as: breadCrumbsLinks.length - 1 === i ? 'span' : Link,
    });
  }

  const nowYear = new Date().getFullYear();
  const titleFolder = t('seo_title_folder', { title: seoName ? seoName : name, year: nowYear });
  const descriptionFolder = t('seo_description_folder', { title: seoName ? seoName : name, year: nowYear });
  const metaData = {
    title: titleFolder,
    description: descriptionFolder,
    keywords: '',
    canonicalUrl: `${process.env.SITE_URL}${router.asPath}`,
  };
  useMetaTags({ lang, ...metaData });

  useEffect(() => {
    if (window.uSocialShare && !document.querySelector('.uscl-bar')) {
      window.uSocialShare && window.uSocialShare.reInit();
    }
    if (window.uSocialShare && document.querySelector('.uscl-bar')) {
      document.querySelector('.uscl-bar').remove();
      window.uSocialShare.reInit();
    }

    if (window.Ya && document.querySelector('.ya-share2') && window.Ya.share2) {
      window.Ya.share2(document.querySelector('.ya-share2'), {
        content: {
          url: `${process.env.SITE_URL}${router.asPath}`,
        },
      });
    }
  }, [router.asPath]);

  return (
    <div className="container">
      <Head>
        <title>{metaData.title}</title>
        <MetaTags {...metaData} />
      </Head>

      <div className={styles.breadCrumbs}>
        <Button
          as={Link}
          viewStyle="secondary"
          className={styles.button}
          iconClassName={styles.buttonIcon}
          icon={<ReactSVG src="/icons/chevron-left-small.svg" wrapper="span" />}
          href="/"
        >
          {!isPhone && 'Назад'}
        </Button>
        <Breadcrumbs items={breadcrumbItems} />
      </div>
      <h1 className={styles.title}>{seoName ? seoName : name}</h1>

      <SearchBlock key={router.asPath} className={styles.searchLine} />
      <Grid columns={3} className={styles.resultBlock} responsive={{ tabletS: 1, phone: 1 }}>
        {folder.children && folder.children.map((folder) => (
          <GridItem key={folder.id}>
            <Element
              href={`/templates/${thisPath}/${folder.translatedName}`}
              {...folder}
            >
              {folder.name}
            </Element>
          </GridItem>
        ))}
        {templates.map((elem) => (
          <GridItem key={elem.id}>
            <Element
              type="doc"
              {...elem}
              href={`/templates/${thisPath}/${elem.translatedName}`}
            >
              {elem.name}
            </Element>
          </GridItem>
        ))}
      </Grid>
      <TextBlockForSeo>
        <h2>{t('seo_h2_folder', { title: seoName ? seoName : name, year: nowYear })}</h2>
        {seo && <div dangerouslySetInnerHTML={{ __html: seo }}></div>}
        <p>{t('seo_paragraph_folder', { title: seoName ? seoName : name, year: nowYear })}</p>
        <SocialMediaButtons />
      </TextBlockForSeo>
    </div>
  );
};

export default FolderPage;
